import React, { useEffect, useMemo, useState } from 'react';
import { useDefaultNewDeviceEntityPath } from '../../hooks/useDefaultNewDeviceEntityPath';
import { SystemEntityPathOptionsState } from '../../hooks/useSystemEntityPathOptions';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import Button from '../ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../pilot-ui/select';

interface DefaultNewDeviceEntityPathPanelProps {
  entityPathOptions: Pick<SystemEntityPathOptionsState, 'options' | 'loading' | 'error'>;
}

const DefaultNewDeviceEntityPathPanel: React.FC<DefaultNewDeviceEntityPathPanelProps> = ({
  entityPathOptions,
}) => {
  const {
    options,
    loading: optionsLoading,
    error: optionsError,
  } = entityPathOptions;
  const {
    path,
    loading: defaultLoading,
    error: defaultError,
    save,
  } = useDefaultNewDeviceEntityPath();
  const [draftPath, setDraftPath] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setDraftPath(path);
  }, [path]);

  const optionPaths = useMemo(() => new Set(options.map((option) => option.path)), [options]);
  const missingOptionPath = draftPath && !optionPaths.has(draftPath) ? draftPath : null;
  const items = [
    { value: '__none__', label: 'No default' },
    ...(missingOptionPath ? [{ value: missingOptionPath, label: `${missingOptionPath} (missing option)` }] : []),
    ...options.map((option) => ({
      value: option.path,
      label: option.label ? `${option.label} (${option.path})` : option.path,
    })),
  ];
  const changed = (draftPath ?? null) !== (path ?? null);
  const loading = optionsLoading || defaultLoading;
  const error = optionsError || defaultError;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await save(draftPath);
    } catch (err) {
      setActionError(extractErrorMessage(err, 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-3">
      {loading && <p className="text-muted-foreground">Loading...</p>}
      {error && <p role="alert" className="text-status-error-foreground text-sm">{error}</p>}
      {actionError && (
        <div role="alert" className="p-2 border border-status-error-border bg-red-50 text-sm text-red-800 rounded-sm">
          {actionError}
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-end">
        <div className="min-w-0 w-full sm:w-auto">
          <label
            htmlFor="default-new-device-entity-path"
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            Default Entity Path
          </label>
          <Select items={items} value={draftPath ?? '__none__'} onValueChange={(value) => setDraftPath(value === '__none__' ? null : value)} disabled={loading || busy}>
            <SelectTrigger id="default-new-device-entity-path" aria-describedby="default-path-help" className="w-full max-w-full sm:w-80" title="Choose the path assigned to new devices that arrive without one.">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
            <SelectItem value="__none__">No default</SelectItem>
            {missingOptionPath && (
              <SelectItem value={missingOptionPath}>{missingOptionPath} (missing option)</SelectItem>
            )}
            {options.map((option) => (
              <SelectItem key={option.path} value={option.path}>
                {option.label ? `${option.label} (${option.path})` : option.path}
              </SelectItem>
            ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="primary"
          type="submit"
          disabled={busy || loading || !changed}
          aria-describedby={!changed ? 'default-save-disabled-reason' : undefined}
          title={changed ? 'Save this as the entity path new devices get when they arrive without one' : 'No changes to save'}
        >
          Save
        </Button>
        {!changed && <p id="default-save-disabled-reason" className="text-xs text-gray-600">No changes to save.</p>}
      </div>
      <p id="default-path-help" className="text-sm text-gray-600">
        New devices get this path only when their entity_path is empty.
      </p>
    </form>
  );
};

export default DefaultNewDeviceEntityPathPanel;
